import React, { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Globe } from "lucide-react";
import useLangRoute from "@/hooks/useLangRoute";
import Logo from "@/components/Logo";

export default function Header() {
  const { t } = useTranslation();
  const { lang, otherPath, path } = useLangRoute();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [lang]);

  const linkCls = ({ isActive }) =>
    `header-link relative text-[14px] font-medium tracking-tight transition-colors ${
      isActive ? "text-[#0F1B2D]" : "text-[#4b5563] hover:text-[#0F1B2D]"
    }`;

  const anchorCls =
    "header-link text-[14px] font-medium tracking-tight text-[#4b5563] hover:text-[#0F1B2D] transition-colors cursor-pointer";

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      // navigate to home then scroll
      window.location.href = `${path("/")}#${id}`;
    }
    setOpen(false);
  };

  return (
    <header
      className={`header-root sticky top-0 z-40 w-full border-b transition-all ${
        scrolled
          ? "bg-[#F7F3EA]/95 backdrop-blur border-[#e6dfcf]"
          : "bg-[#F7F3EA] border-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
        <Link to={path("/")} data-testid="header-logo" className="header-link flex items-center" aria-label="Mirexiluna">
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          <NavLink to={path("/")} end className={linkCls} data-testid="nav-home">
            {t("nav.home")}
          </NavLink>
          <a href={`${path("/")}#how`} onClick={scrollTo("how")} className={anchorCls} data-testid="nav-how">
            {t("nav.how")}
          </a>
          <a href={`${path("/")}#projects`} onClick={scrollTo("projects")} className={anchorCls} data-testid="nav-projects">
            {t("nav.projects")}
          </a>
          <a href={`${path("/")}#about`} onClick={scrollTo("about")} className={anchorCls} data-testid="nav-about">
            {t("nav.about")}
          </a>
          <a href={`${path("/")}#contact`} onClick={scrollTo("contact")} className={anchorCls} data-testid="nav-contact">
            {t("nav.contact")}
          </a>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to={otherPath}
            data-testid="lang-switch"
            className="header-link inline-flex items-center gap-1.5 rounded-full border border-[#0F1B2D]/15 px-3 py-1.5 text-[13px] font-medium text-[#0F1B2D] hover:bg-[#0F1B2D] hover:text-[#F7F3EA] transition-colors"
            aria-label="Language switch"
          >
            <Globe className="h-3.5 w-3.5" />
            {lang === "ar" ? "EN" : "ES"}
          </Link>
          <a
            href={`${path("/")}#contact`}
            onClick={scrollTo("contact")}
            data-testid="nav-cta"
            className="header-link inline-flex items-center rounded-full bg-[#0F1B2D] px-4 py-2 text-[13px] font-semibold text-[#F7F3EA] hover:bg-[#1c2c46] transition-colors"
          >
            {t("hero.ctaPrimary")}
          </a>
        </div>

        <button
          className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md text-[#0F1B2D] header-link"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? t("nav.close") : t("nav.openMenu")}
          data-testid="header-hamburger"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#e6dfcf] bg-[#F7F3EA]" data-testid="mobile-menu">
          <div className="mx-auto max-w-6xl px-5 py-4 flex flex-col gap-3">
            <NavLink to={path("/")} end className={linkCls} onClick={() => setOpen(false)}>
              {t("nav.home")}
            </NavLink>
            <a href={`${path("/")}#how`} onClick={scrollTo("how")} className={anchorCls}>
              {t("nav.how")}
            </a>
            <a href={`${path("/")}#projects`} onClick={scrollTo("projects")} className={anchorCls}>
              {t("nav.projects")}
            </a>
            <a href={`${path("/")}#about`} onClick={scrollTo("about")} className={anchorCls}>
              {t("nav.about")}
            </a>
            <a href={`${path("/")}#contact`} onClick={scrollTo("contact")} className={anchorCls}>
              {t("nav.contact")}
            </a>
            <div className="flex items-center gap-3 pt-2">
              <Link
                to={otherPath}
                onClick={() => setOpen(false)}
                className="header-link inline-flex items-center gap-1.5 rounded-full border border-[#0F1B2D]/15 px-3 py-1.5 text-[13px] font-medium text-[#0F1B2D]"
                data-testid="lang-switch-mobile"
              >
                <Globe className="h-3.5 w-3.5" />
                {lang === "ar" ? "EN" : "ES"}
              </Link>
              <a
                href={`${path("/")}#contact`}
                onClick={scrollTo("contact")}
                className="header-link inline-flex items-center rounded-full bg-[#0F1B2D] px-4 py-2 text-[13px] font-semibold text-[#F7F3EA]"
              >
                {t("hero.ctaPrimary")}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
